import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import type { UserQuery, UserType } from '@/gql/graphql';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { refuseAll, screenAll, useEmailGuard, type EmailGuard } from '@/lib/useEmailCheck';
import { CreateUserDoc, UpdateUserDoc } from './userDocuments';

export type UserRecord = NonNullable<UserQuery['user']>;

export type AddressField = 'email' | 'invoiceEmail';

export interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  type: string;
  allowedScopeIds: string[];
  defaultScopeId: string;
  defaultPage: string;
  dateOfBirth: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  gender: string;
  pronouns: string;
  iban: string;
  chamberOfCommerce: string;
  vatNumber: string;
  invoiceEmail: string;
}

const EMPTY_VALUES: UserFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  type: '',
  allowedScopeIds: [],
  defaultScopeId: '',
  defaultPage: '',
  dateOfBirth: '',
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  country: '',
  gender: '',
  pronouns: '',
  iban: '',
  chamberOfCommerce: '',
  vatNumber: '',
  invoiceEmail: '',
};

function valuesFromUser(user: UserRecord): UserFormValues {
  return {
    firstName: user.firstName,
    lastName: user.lastName ?? '',
    email: user.email,
    type: user.type ?? '',
    allowedScopeIds: user.allowedScopes.map((scope) => scope.id),
    defaultScopeId: user.defaultScope?.id ?? '',
    defaultPage: user.defaultPage ?? '',
    dateOfBirth: user.personal?.dateOfBirth ?? '',
    phone: user.personal?.phone ?? '',
    address: user.personal?.address ?? '',
    postalCode: user.personal?.postalCode ?? '',
    city: user.personal?.city ?? '',
    country: user.personal?.country ?? '',
    gender: user.personal?.gender ?? '',
    pronouns: user.personal?.pronouns ?? '',
    iban: user.business?.iban ?? '',
    chamberOfCommerce: user.business?.chamberOfCommerce ?? '',
    vatNumber: user.business?.vatNumber ?? '',
    invoiceEmail: user.business?.invoiceEmail ?? '',
  };
}

/** Exact Online relation fields only apply to the two invoiced types. */
export function hasBusinessFields(type: string): boolean {
  return type === 'EMPLOYEE' || type === 'CONTRACTOR';
}

export const USER_TYPE_FIELD_DESCRIPTION =
  'Customer, Employee, and Contractor save here. Admin is assigned with the Admin or Super Admin role on Access — that is how you promote. Saving Customer clears venue grants.';

export const USER_TYPE_OPTIONS = [
  { value: '', label: 'Customer' },
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'CONTRACTOR', label: 'Contractor' },
  { value: 'ADMIN', label: 'Admin' },
];

export interface UseUserFormOptions {
  user: UserRecord | null;
  id?: string;
  isNew: boolean;
  onCreated: (id: string) => void;
  onSaved: () => Promise<unknown>;
}

/**
 * Owns the edit state for one user. Information and Access are two tabs over
 * the same record, so the state lives above them and either tab's Save
 * persists every field.
 */
export function useUserForm({
  user,
  id,
  isNew,
  onCreated,
  onSaved,
}: UseUserFormOptions) {
  const [values, setValues] = useState<UserFormValues>(() =>
    user ? valuesFromUser(user) : EMPTY_VALUES,
  );
  const [error, setError] = useState<string | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(user?.id ?? null);

  if (user && user.id !== seededFor) {
    setSeededFor(user.id);
    setValues(valuesFromUser(user));
  }

  const [createUser, { loading: creating }] = useMutation(CreateUserDoc);
  const [updateUser, { loading: updating }] = useMutation(UpdateUserDoc);

  const [checking, setChecking] = useState(false);
  const emailGuard = useEmailGuard();
  const invoiceEmailGuard = useEmailGuard();
  const guards: Record<AddressField, EmailGuard> = {
    email: emailGuard,
    invoiceEmail: invoiceEmailGuard,
  };

  function set<K extends keyof UserFormValues>(
    key: K,
    value: UserFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (key === 'email') emailGuard.changed();
    if (key === 'invoiceEmail') invoiceEmailGuard.changed();
  }

  /**
   * The addresses about to be saved, for the submit-time check. An edit leaves
   * an unchanged address alone; the mutation still refuses a dead domain.
   */
  const addressesToScreen = (next: UserFormValues) => {
    const email = next.email.trim();
    const invoiceEmail = next.invoiceEmail.trim();
    return [
      [emailGuard, isNew || email !== (user?.email ?? '') ? email : null],
      [
        invoiceEmailGuard,
        hasBusinessFields(next.type) &&
        invoiceEmail !== (user?.business?.invoiceEmail ?? '')
          ? invoiceEmail
          : null,
      ],
    ] as const;
  };

  /** Use / Keep on a prompt: set the field to the picked address and save at once. */
  const resubmit = (field: AddressField, address: string) => {
    const next = { ...values, [field]: address };
    setValues(next);
    void save(next);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await save(values);
  };

  const save = async (values: UserFormValues) => {
    setError(null);
    setChecking(true);
    try {
      if (!(await screenAll(addressesToScreen(values)))) return;
      await persist(values);
    } finally {
      setChecking(false);
    }
  };

  const persist = async (values: UserFormValues) => {
    const isCustomer = !values.type;
    const base = {
      email: values.email.trim(),
      lastName: values.lastName.trim() || null,
      type: (values.type || null) as UserType | null,
      allowedScopeIds: isCustomer ? [] : values.allowedScopeIds,
      defaultScopeId: isCustomer ? null : values.defaultScopeId || null,
      defaultPage: values.defaultPage || null,
    };

    try {
      if (isNew) {
        const result = await createUser({
          variables: {
            input: { ...base, firstName: values.firstName.trim() },
          },
        });
        const newId = result.data?.createUser.id;
        if (newId) onCreated(newId);
        return;
      }
      if (!id) return;

      const business = hasBusinessFields(values.type);
      // A provisional account has no first name, so edit never requires one.
      // Send it only when changed; the backend accepts blank on update.
      const firstName = values.firstName.trim();
      const firstNameChanged = firstName !== (user?.firstName ?? '').trim();
      await updateUser({
        variables: {
          id,
          input: {
            ...base,
            firstName: firstNameChanged ? firstName : undefined,
            dateOfBirth: values.dateOfBirth.trim() || null,
            phone: values.phone.trim() || null,
            address: values.address.trim() || null,
            postalCode: values.postalCode.trim() || null,
            city: values.city.trim() || null,
            country: values.country.trim() || null,
            gender: values.gender.trim() || null,
            pronouns: values.pronouns.trim() || null,
            iban: business ? values.iban.trim() || null : undefined,
            chamberOfCommerce: business
              ? values.chamberOfCommerce.trim() || null
              : undefined,
            vatNumber: business ? values.vatNumber.trim() || null : undefined,
            invoiceEmail: business
              ? values.invoiceEmail.trim() || null
              : undefined,
          },
        },
      });
      await onSaved();
    } catch (err) {
      refuseAll(err, [
        [emailGuard, values.email.trim()],
        [invoiceEmailGuard, values.invoiceEmail.trim()],
      ]);
      setError(graphqlErrorMessage(err));
    }
  };

  return {
    values,
    set,
    submit,
    saving: creating || updating || checking,
    error,
    setError,
    guards,
    resubmit,
  };
}

export type UserForm = ReturnType<typeof useUserForm>;
